import React, { useState, useRef, useEffect } from 'react';
import { 
  Filter, X, ChevronDown, Check, Save, RotateCcw, Star, Trash2,
  Sliders, Search
} from 'lucide-react';
import { api } from '../api/client';

/**
 * EnhancedFilterBarV2 - Always expanded filter bar with confidence range slider
 * Supports multi-select, health categories, and filter presets
 */
const EnhancedFilterBarV2 = ({
  filters = {},
  options = {},
  onChange,
  onClear,
  dashboardType = 'pmo'
}) => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [presets, setPresets] = useState([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showPresetsDropdown, setShowPresetsDropdown] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [isShared, setIsShared] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const presetsRef = useRef(null);
  const dropdownRefs = useRef({});
  
  const filterCount = Object.entries(filters).filter(([key, v]) => {
    if (key === 'confidence_min' || key === 'confidence_max') return false;
    return Array.isArray(v) ? v.length > 0 : v !== '' && v !== null && v !== undefined;
  }).length;

  // Check if confidence range is not default
  const hasConfidenceFilter = filters.confidence_min > 0 || filters.confidence_max < 100;
  const totalActiveFilters = filterCount + (hasConfidenceFilter ? 1 : 0);

  useEffect(() => {
    loadPresets();
  }, [dashboardType]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (presetsRef.current && !presetsRef.current.contains(event.target)) {
        setShowPresetsDropdown(false);
      }
      // Close dropdown if clicked outside
      Object.entries(dropdownRefs.current).forEach(([key, ref]) => {
        if (ref && !ref.contains(event.target)) {
          if (openDropdown === key) setOpenDropdown(null);
        }
      });
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);

  const loadPresets = async () => {
    try {
      const res = await api.getFilterPresets({ dashboard_type: dashboardType });
      setPresets(res.data.presets || []);
    } catch (err) {
      console.error('Failed to load filter presets:', err);
    }
  };

  const handleSavePreset = async () => {
    if (!presetName.trim()) return;
    setSaving(true);
    try {
      await api.createFilterPreset({
        name: presetName.trim(),
        filters: filters,
        dashboard_type: dashboardType,
        is_shared: isShared
      });
      await loadPresets();
      setShowSaveModal(false);
      setPresetName('');
      setIsShared(false);
    } catch (err) {
      console.error('Failed to save preset:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleLoadPreset = (preset) => {
    onChange(preset.filters);
    setShowPresetsDropdown(false);
  };

  const handleDeletePreset = async (presetId, e) => {
    e.stopPropagation();
    try {
      await api.deleteFilterPreset(presetId);
      await loadPresets();
    } catch (err) {
      console.error('Failed to delete preset:', err);
    }
  };

  // Base filter configs - always shown
  const baseFilterConfigs = [
    { key: 'portfolio_ids', label: 'Portfolio', options: options.portfolios || [], multi: true },
    { key: 'program_ids', label: 'Program', options: options.programs || [], multi: true },
    { key: 'project_ids', label: 'Project', options: options.projects || [], multi: true },
    { key: 'pm_ids', label: 'PM', options: options.project_managers || [], multi: true },
    { key: 'sprint_ids', label: 'Sprint', options: options.sprints || [], multi: true },
    { key: 'release_ids', label: 'Release', options: options.releases || [], multi: true },
    { key: 'statuses', label: 'Status', options: [
      { id: 'Blocked', name: 'Blocked' },
      { id: 'Behind', name: 'Behind' },
      { id: 'At Risk', name: 'At Risk' },
      { id: 'In Progress', name: 'In Progress' },
      { id: 'On Track', name: 'On Track' },
      { id: 'Completed', name: 'Completed' }
    ], multi: true },
    { key: 'health_categories', label: 'Health', options: [
      { id: 'Red', name: 'Red (Critical)' },
      { id: 'Amber', name: 'Amber (At Risk)' },
      { id: 'Green', name: 'Green (Healthy)' }
    ], multi: true }
  ];

  // D) Source filter - for PM dashboard (data origin: Excel, Manual, DevOps, Jira)
  const sourceFilterConfig = { 
    key: 'sources', 
    label: 'Source', 
    options: options.sources || [
      { id: 'Excel', name: 'Excel Import' },
      { id: 'Manual', name: 'Manual Entry' },
      { id: 'DevOps', name: 'Azure DevOps' },
      { id: 'Jira', name: 'Jira' }
    ], 
    multi: true 
  };

  // Combine filters based on dashboard type
  const filterConfigs = dashboardType === 'pm' 
    ? [...baseFilterConfigs, sourceFilterConfig]
    : baseFilterConfigs;

  const handleFilterChange = (key, value, isMulti) => {
    if (isMulti) {
      const currentValues = filters[key] || [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter(v => v !== value)
        : [...currentValues, value];
      onChange({ ...filters, [key]: newValues });
    } else {
      onChange({ ...filters, [key]: value === filters[key] ? '' : value });
    }
  };

  const handleClearAll = () => {
    const clearedFilters = {
      confidence_min: 0,
      confidence_max: 100
    };
    filterConfigs.forEach(f => {
      clearedFilters[f.key] = f.multi ? [] : '';
    });
    onChange(clearedFilters);
    if (onClear) onClear();
  };

  const getFilterLabel = (config) => {
    const value = filters[config.key];
    if (!value || (Array.isArray(value) && value.length === 0)) {
      return config.label;
    }
    if (config.multi && Array.isArray(value)) {
      if (value.length === 1) {
        const opt = config.options.find(o => o.id === value[0]);
        return opt?.name?.split(' ')[0] || value[0];
      }
      return `${config.label} (${value.length})`;
    }
    const opt = config.options.find(o => o.id === value);
    return opt?.name?.split(' ')[0] || value;
  };

  const isFilterActive = (config) => {
    const value = filters[config.key];
    return Array.isArray(value) ? value.length > 0 : value !== '' && value !== null && value !== undefined;
  };

  return (
    <div className="bg-[var(--clr-surface-card)] border-b border-[var(--clr-border-light)] sticky top-0 z-30" data-testid="enhanced-filter-bar-v2">
      <div className="px-4 py-2">
        <div className="flex items-center gap-2 flex-wrap">
          {/* Filter Icon & Count */}
          <div className="flex items-center gap-1.5 text-[var(--clr-text-secondary)] pr-2 border-r border-[var(--clr-border-light)]">
            <Filter className="w-4 h-4" />
            <span className="text-sm font-medium">
              Filters {totalActiveFilters > 0 && <span className="text-[var(--clr-brand-primary)] font-semibold">({totalActiveFilters})</span>}
            </span>
          </div>

          {/* Saved Presets */}
          {presets.length > 0 && (
            <div className="relative" ref={presetsRef}>
              <button
                onClick={() => setShowPresetsDropdown(!showPresetsDropdown)}
                className="flex items-center gap-1 px-2 py-1 text-xs rounded border border-[var(--clr-sev-atrisk)] bg-[var(--clr-sev-atrisk-bg)] text-[var(--clr-sev-atrisk)] hover:opacity-80"
                data-testid="presets-dropdown-btn"
              >
                <Star className="w-3 h-3" />
                <span>Presets</span>
                <ChevronDown className={`w-3 h-3 transition-transform ${showPresetsDropdown ? 'rotate-180' : ''}`} />
              </button>
              
              {showPresetsDropdown && (
                <div className="absolute top-full left-0 mt-1 w-52 bg-[var(--clr-surface-card)] rounded-lg shadow-[var(--shadow-dropdown)] border border-[var(--clr-border-light)] py-1 z-50 max-h-60 overflow-y-auto">
                  {presets.map(preset => (
                    <div
                      key={preset.id}
                      onClick={() => handleLoadPreset(preset)}
                      className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-[var(--clr-surface-hover)] cursor-pointer group"
                    >
                      <div className="flex items-center gap-2 truncate">
                        <Star className={`w-3 h-3 ${preset.is_shared ? 'text-[var(--clr-sev-atrisk)]' : 'text-[var(--clr-text-muted)]'}`} />
                        <span className="truncate text-[var(--clr-text-primary)]">{preset.name}</span>
                      </div>
                      <button
                        onClick={(e) => handleDeletePreset(preset.id, e)}
                        className="opacity-0 group-hover:opacity-100 p-1 hover:bg-[var(--clr-sev-blocked-bg)] rounded"
                      >
                        <Trash2 className="w-3 h-3 text-[var(--clr-sev-blocked)]" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Filter Dropdowns */}
          {filterConfigs.map(config => (
            <FilterDropdownV2
              key={config.key}
              config={config}
              value={filters[config.key]}
              isActive={isFilterActive(config)}
              label={getFilterLabel(config)}
              isOpen={openDropdown === config.key}
              onToggle={() => setOpenDropdown(openDropdown === config.key ? null : config.key)}
              onChange={(value) => handleFilterChange(config.key, value, config.multi)}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              dropdownRef={(el) => dropdownRefs.current[config.key] = el}
            />
          ))}

          {/* Confidence Range Slider */}
          <ConfidenceRangeFilter
            min={filters.confidence_min ?? 0}
            max={filters.confidence_max ?? 100}
            onChange={(min, max) => onChange({ ...filters, confidence_min: min, confidence_max: max })}
            isOpen={openDropdown === 'confidence'}
            onToggle={() => setOpenDropdown(openDropdown === 'confidence' ? null : 'confidence')}
          />

          {/* Reset All */}
          {totalActiveFilters > 0 && (
            <button
              onClick={handleClearAll}
              className="flex items-center gap-1 px-2 py-1 text-xs text-[var(--clr-text-secondary)] hover:text-[var(--clr-text-primary)] hover:bg-[var(--clr-surface-hover)] rounded transition-colors"
              data-testid="reset-all-filters"
            >
              <RotateCcw className="w-3 h-3" />
              Reset
            </button>
          )}

          {/* Save Preset */}
          {totalActiveFilters > 0 && (
            <button
              onClick={() => setShowSaveModal(true)}
              className="flex items-center gap-1 px-2 py-1 text-xs text-[var(--clr-brand-primary)] hover:bg-[var(--clr-brand-primary)] bg-opacity-10 rounded ml-auto"
              data-testid="save-preset-btn"
            >
              <Save className="w-3 h-3" />
              Save
            </button>
          )}
        </div>
      </div>

      {/* Save Modal */}
      {showSaveModal && (
        <>
          <div className="fixed inset-0 bg-black/30 z-40" onClick={() => setShowSaveModal(false)} />
          <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-[var(--clr-surface-card)] rounded-xl shadow-2xl border p-5 z-50 w-80" data-testid="save-preset-modal">
            <h3 className="text-base font-semibold text-[var(--clr-text-primary)] mb-3">Save Filter Preset</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-[var(--clr-text-secondary)] mb-1">Preset Name</label>
                <input
                  type="text"
                  value={presetName}
                  onChange={(e) => setPresetName(e.target.value)}
                  placeholder="e.g., Q1 At-Risk Programs"
                  className="w-full px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)]"
                  autoFocus
                />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isShared}
                  onChange={(e) => setIsShared(e.target.checked)}
                  className="w-3.5 h-3.5 rounded border-slate-300 text-[var(--clr-brand-primary)]"
                />
                <span className="text-xs text-[var(--clr-text-secondary)]">Share with team</span>
              </label>
              <div className="flex gap-2 pt-1">
                <button
                  onClick={() => setShowSaveModal(false)}
                  className="flex-1 px-3 py-1.5 text-sm border rounded-lg text-[var(--clr-text-secondary)] hover:bg-[var(--clr-surface-page)]"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSavePreset}
                  disabled={!presetName.trim() || saving}
                  className="flex-1 px-3 py-1.5 text-sm bg-[var(--clr-brand-primary)] text-white rounded-lg hover:bg-[var(--clr-brand-primary-hover)] disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Filter Dropdown V2
const FilterDropdownV2 = ({ config, value, isActive, label, isOpen, onToggle, onChange, searchTerm, onSearchChange, dropdownRef }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (dropdownRef) dropdownRef(ref.current);
  }, [dropdownRef]);

  const filteredOptions = config.options.filter(opt => 
    (opt.name || '').toLowerCase().includes((searchTerm || '').toLowerCase())
  );

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={onToggle}
        className={`flex items-center gap-1 px-2 py-1 text-xs rounded-md border transition-colors ${
          isActive
            ? 'bg-[var(--clr-brand-primary)] text-[var(--clr-text-on-brand)] border-[var(--clr-brand-primary)]'
            : 'bg-white border-[var(--clr-border-light)] text-[var(--clr-text-secondary)] hover:border-slate-300'
        }`}
      >
        <span>{label}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-48 bg-[var(--clr-surface-card)] rounded-lg shadow-lg border z-50 max-h-60 overflow-hidden">
          {config.options.length > 5 && (
            <div className="p-2 border-b">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-[var(--clr-text-muted)]" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => onSearchChange(e.target.value)}
                  placeholder="Search..."
                  className="w-full pl-7 pr-2 py-1 text-xs border rounded focus:ring-1 focus:ring-[var(--clr-brand-primary)]"
                />
              </div>
            </div>
          )}
          <div className="max-h-48 overflow-y-auto py-1">
            {filteredOptions.map(option => {
              const isSelected = Array.isArray(value) ? value.includes(option.id) : value === option.id;
              return (
                <button
                  key={option.id}
                  onClick={() => onChange(option.id)}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left transition-colors ${
                    isSelected 
                      ? 'bg-[var(--clr-brand-primary)] text-[var(--clr-text-on-brand)]' 
                      : 'text-[var(--clr-text-secondary)] hover:bg-[var(--clr-surface-page)]'
                  }`}
                >
                  <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${
                    isSelected ? 'bg-white border-white' : 'border-slate-300 bg-white'
                  }`}>
                    {isSelected && <Check className="w-2.5 h-2.5 text-[var(--clr-brand-primary)]" />}
                  </div>
                  <span className="truncate">{option.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Confidence Range Filter
const ConfidenceRangeFilter = ({ min, max, onChange, isOpen, onToggle }) => {
  const [localMin, setLocalMin] = useState(min);
  const [localMax, setLocalMax] = useState(max);
  const ref = useRef(null);

  useEffect(() => {
    setLocalMin(min);
    setLocalMax(max);
  }, [min, max]);

  const hasFilter = min > 0 || max < 100;
  const label = hasFilter ? `${min}-${max}%` : 'Confidence';

  const handleApply = () => {
    onChange(localMin, localMax);
    onToggle();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={onToggle}
        className={`flex items-center gap-1 px-2 py-1 text-xs rounded-md border transition-colors ${
          hasFilter
            ? 'bg-[var(--clr-brand-primary)] text-[var(--clr-text-on-brand)] border-[var(--clr-brand-primary)]'
            : 'bg-white border-[var(--clr-border-light)] text-[var(--clr-text-secondary)] hover:border-slate-300'
        }`}
      >
        <Sliders className="w-3 h-3" />
        <span>{label}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-56 bg-[var(--clr-surface-card)] rounded-lg shadow-lg border p-3 z-50">
          <div className="text-xs font-medium text-[var(--clr-text-secondary)] mb-2">Confidence Range</div>
          
          <div className="flex items-center gap-2 mb-3">
            <input
              type="number"
              value={localMin}
              onChange={(e) => setLocalMin(Math.max(0, Math.min(100, parseInt(e.target.value) || 0)))}
              className="w-16 px-2 py-1 text-xs border rounded text-center"
              min="0"
              max="100"
            />
            <span className="text-[var(--clr-text-muted)]">to</span>
            <input
              type="number"
              value={localMax}
              onChange={(e) => setLocalMax(Math.max(0, Math.min(100, parseInt(e.target.value) || 100)))}
              className="w-16 px-2 py-1 text-xs border rounded text-center"
              min="0"
              max="100"
            />
            <span className="text-xs text-[var(--clr-text-muted)]">%</span>
          </div>

          <div className="relative h-2 bg-[var(--clr-border-light)] rounded-full mb-3">
            <div
              className="absolute h-full bg-[var(--clr-brand-primary)] bg-opacity-100 rounded-full"
              style={{
                left: `${localMin}%`,
                width: `${localMax - localMin}%`
              }}
            />
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => { setLocalMin(0); setLocalMax(100); }}
              className="flex-1 px-2 py-1 text-xs border rounded text-[var(--clr-text-secondary)] hover:bg-[var(--clr-surface-page)]"
            >
              Reset
            </button>
            <button
              onClick={handleApply}
              className="flex-1 px-2 py-1 text-xs bg-[var(--clr-brand-primary)] text-white rounded hover:bg-[var(--clr-brand-primary-hover)]"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedFilterBarV2;
